import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  ArrowBack as BackIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { ExcelUpload as ExcelUploadType } from '../types';
import * as XLSX from 'xlsx';

const ExcelUploadComponent: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  const { data: uploads = [] } = useQuery<ExcelUploadType[]>({
    queryKey: ['excelUploads'],
    queryFn: () => apiService.getUploads().then(response => response.uploads),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => apiService.uploadFile(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['excelUploads'] });
      setSelectedFile(null);
    },
  });

  const processMutation = useMutation({
    mutationFn: (uploadId: number) => apiService.processUpload(uploadId),
    onSuccess: (data) => {
      if (data.warning) {
        setWarningMessage(data.warning);
      }
      // Depuración: imprime la respuesta y la caché anterior
      console.log('Respuesta de process:', data);
      // Actualiza la caché localmente con el upload actualizado
      queryClient.setQueryData(['excelUploads'], (oldData: any) => {
        if (!oldData) return oldData;
        console.log('Caché anterior:', oldData);
        return {
          ...oldData,
          uploads: oldData.uploads.map((upload: ExcelUploadType) =>
            upload.id === data.upload.id ? { ...upload, ...data.upload } : upload
          )
        };
      });
      // Luego, fuerza la recarga para mantener la consistencia
      queryClient.invalidateQueries({ queryKey: ['excelUploads'] });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['excelUploads'] });
    }
  });

  const resetMutation = useMutation({
    mutationFn: (uploadId: number) => apiService.resetUpload(uploadId),
    onSuccess: () => {
      // Refresca la lista de archivos subidos al completar el reset
      queryClient.invalidateQueries({ queryKey: ['excelUploads'] });
    },
    onError: () => {
      // También refresca la lista si ocurre un error al resetear
      queryClient.invalidateQueries({ queryKey: ['excelUploads'] });
    }
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setSelectedFile(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadMutation.mutate(selectedFile);
    }
  };

  const handleProcess = (uploadId: number) => {
    processMutation.mutate(uploadId);
  };

  const handleReset = (uploadId: number) => {
    resetMutation.mutate(uploadId);
  };

  // Utilidad para extraer el mensaje de error
  const getErrorMessage = (error: unknown): string => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message;
    // Si viene de una respuesta de axios/fetch
    if ((error as any).response && (error as any).response.data) {
      const data = (error as any).response.data;
      if (typeof data === 'string') return data;
      if (data.message) return data.message;
      if (data.error) return data.error;
    }
    // Si viene de tanstack query
    if ((error as any).message) return (error as any).message;
    return 'Ocurrió un error desconocido';
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/dashboard')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Carga de Archivos Excel
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Aquí podrá importar los topes de descuentos por cliente.</strong> Debe subir un archivo Excel con los datos correspondientes al formato pre establecido. Para eso, puede usar el archivo Excel de ejemplo.
              </Typography>
            </Alert>
            
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileIcon />}
                onClick={() => {
                  // Crear workbook
                  const workbook = XLSX.utils.book_new();

                  // Hoja 1: Instrucciones de uso (ahora es la primera)
                  const worksheet1 = XLSX.utils.aoa_to_sheet([
                    ['INSTRUCCIONES DE USO', '', ''],
                    ['', '', ''],
                    ['Prioridad de Búsqueda:', '', ''],
                    ['1. Cliente + Producto específico', 'Máxima prioridad', 'Si existe, se usa este tope'],
                    ['2. Cliente + Rubro', 'Prioridad media', 'Si no hay producto específico, se busca por rubro'],
                    ['3. Cliente general', 'Prioridad baja', 'Si no hay rubro específico, se usa el tope del cliente'],
                    ['4. Tope por defecto', 'Mínima prioridad', 'Si no hay configuración, se usa 0%'],
                    ['', '', ''],
                    ['Formato de datos por hoja:', '', ''],
                    ['', '', ''],
                    ['HOJA 2 - Cliente-Producto:', '', ''],
                    ['- Cliente: Código único del cliente', '', ''],
                    ['- Código Producto: Código del producto específico', '', ''],
                    ['- Descripción Producto: Nombre/descripción del producto', '', ''],
                    ['- Tope Descuento: Porcentaje máximo permitido', '', ''],
                    ['', '', ''],
                    ['HOJA 3 - Cliente-Rubro:', '', ''],
                    ['- Cliente: Código único del cliente', '', ''],
                    ['- Rubro: Categoría del producto', '', ''],
                    ['- Descripción Rubro: Nombre/descripción del rubro', '', ''],
                    ['- Tope Descuento: Porcentaje máximo permitido', '', ''],
                    ['', '', ''],
                    ['HOJA 4 - Cliente-General:', '', ''],
                    ['- Cliente: Código único del cliente', '', ''],
                    ['- Tope Descuento: Porcentaje máximo permitido para todos los rubros', '', ''],
                    ['', '', ''],
                    ['Notas importantes:', '', ''],
                    ['- El sistema busca en orden de prioridad: Producto > Rubro > General > 0%', '', ''],
                    ['- Si no encuentra configuración específica, usa 0% por defecto', '', ''],
                    ['- Los códigos de cliente deben ser consistentes en todas las hojas', '', ''],
                    ['- Los porcentajes pueden ser decimales (ej: 15,5; 20,25; 12,75)', '', ''],
                    ['- Usar coma como separador decimal (ej: 15,5, NO 15.5)', '', '']
                  ]);
                  worksheet1['!cols'] = [{ wch: 35 }, { wch: 25 }, { wch: 45 }];
                  XLSX.utils.book_append_sheet(workbook, worksheet1, 'Instrucciones');

                  // Hoja 2: Topes por Cliente y Producto (ahora es la segunda)
                  const worksheet2 = XLSX.utils.aoa_to_sheet([
                    ['Cliente', 'Código Producto', 'Descripción Producto', 'Tope Descuento (%)'],
                    ['CLI001', 'PROD001', 'Producto Específico A', '25,5'],
                    ['CLI002', 'PROD002', 'Producto Específico B', '30,25'],
                    ['CLI003', 'PROD003', 'Producto Específico C', '15,75']
                  ]);
                  worksheet2['!cols'] = [{ wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 18 }];
                  XLSX.utils.book_append_sheet(workbook, worksheet2, 'Cliente-Producto');

                  // Hoja 3: Topes por Cliente y Rubro (ahora es la tercera)
                  const worksheet3 = XLSX.utils.aoa_to_sheet([
                    ['Cliente', 'Rubro', 'Descripción Rubro', 'Tope Descuento (%)'],
                    ['CLI001', 'ELECTRONICA', 'Productos Electrónicos', '20,5'],
                    ['CLI002', 'ROPA', 'Vestimenta y Accesorios', '18,25'],
                    ['CLI003', 'HOGAR', 'Artículos para el Hogar', '12,75'],
                    ['CLI004', 'DEPORTES', 'Artículos Deportivos', '22,0']
                  ]);
                  worksheet3['!cols'] = [{ wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 18 }];
                  XLSX.utils.book_append_sheet(workbook, worksheet3, 'Cliente-Rubro');

                  // Hoja 4: Topes por Cliente general (ahora es la cuarta)
                  const worksheet4 = XLSX.utils.aoa_to_sheet([
                    ['Cliente', 'Tope Descuento (%)'],
                    ['CLI001', '15,5'],
                    ['CLI002', '10,25'],
                    ['CLI003', '20,75'],
                    ['CLI004', '8,0'],
                    ['CLI005', '12,5']
                  ]);
                  worksheet4['!cols'] = [{ wch: 12 }, { wch: 18 }];
                  XLSX.utils.book_append_sheet(workbook, worksheet4, 'Cliente-General');

                  // Generar archivo Excel
                  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

                  // Descargar archivo
                  const link = document.createElement('a');
                  const url = URL.createObjectURL(blob);
                  link.setAttribute('href', url);
                  link.setAttribute('download', 'GLOWIX_TOPE_DESCUENTOS.xlsx');
                  link.style.visibility = 'hidden';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                }}
                sx={{ mb: 2 }}
              >
                Descargar Archivo de Ejemplo (.xlsx)
              </Button>
              <Typography variant="caption" display="block" color="text.secondary">
                Descargue este archivo para ver el formato requerido con jerarquía de descuentos
              </Typography>
            </Box>
            
            <Box
              sx={{
                border: '2px dashed',
                borderColor: dragActive ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: dragActive ? 'action.hover' : 'background.paper',
                transition: 'all 0.2s',
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Arrastra y suelta tu archivo aquí
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                o
              </Typography>
              <Button
                variant="contained"
                component="label"
                sx={{ mt: 1 }}
              >
                Seleccionar Archivo
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                />
              </Button>
            </Box>

            {selectedFile && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info">
                  Archivo seleccionado: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </Alert>
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={uploadMutation.isPending}
                  sx={{ mt: 2 }}
                >
                  {uploadMutation.isPending ? <CircularProgress size={20} /> : 'Subir Archivo'}
                </Button>
              </Box>
            )}

            {uploadMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Error al subir el archivo:
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {getErrorMessage(uploadMutation.error)}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Archivos Subidos
            </Typography>
            
            {uploads.length === 0 ? (
              <Typography color="text.secondary" align="center">
                No hay archivos subidos
              </Typography>
            ) : (
              <List>
                {uploads.map((upload) => (
                  <ListItem key={upload.id} divider alignItems="flex-start" sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                      <ListItemIcon>
                        <FileIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={upload.fileName}
                        secondary={
                          <Box>
                            <Typography variant="body2">
                              Tamaño: {(upload.fileSize / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                            <Typography variant="body2">
                              Subido: {new Date(upload.uploadedAt).toLocaleString()}
                            </Typography>
                            <Typography variant="body2">
                              Estado: {upload.status}
                            </Typography>
                            {upload.processedRows > 0 && (
                              <Typography variant="body2">
                                Filas procesadas: {upload.processedRows} / {upload.totalRows}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      {/* Botón o texto alineado y con mismo ancho */}
                      <Box sx={{ minWidth: 120, display: 'flex', justifyContent: 'flex-end' }}>
                        {upload.status === 'Pending' && (
                          <Button variant="outlined" onClick={() => handleProcess(upload.id)} disabled={processMutation.isPending}>
                            Procesar
                          </Button>
                        )}
                        {upload.status === 'Processing' && (
                          <Button variant="outlined" color="warning" onClick={() => handleReset(upload.id)} disabled={resetMutation.isPending}>
                            Resetear
                          </Button>
                        )}
                        {upload.status === 'Failed' && (
                          <Button variant="outlined" color="error" onClick={() => handleProcess(upload.id)} disabled={processMutation.isPending}>
                            Reintentar
                          </Button>
                        )}
                        {upload.status === 'Completed' && (
                          <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', minWidth: 120, justifyContent: 'center' }}>
                            ✓ Completado
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {/* Mensaje de error o advertencia ocupando toda la fila */}
                    {(upload.status === 'Failed' && upload.errorMessage) && (
                      <Alert severity="error" sx={{ width: '100%', mt: 1 }}>
                        {upload.errorMessage}
                      </Alert>
                    )}
                    {(upload.status === 'Completed' && upload.errorMessage) && (
                      <Alert severity="warning" sx={{ width: '100%', mt: 1 }}>
                        {upload.errorMessage}
                      </Alert>
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
        {warningMessage && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {warningMessage}
          </Alert>
        )}
        {processMutation.isPending && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Procesando archivo, esto puede demorar varios segundos...
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default ExcelUploadComponent; 